import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAppContext } from "@/providers";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SiteHeader } from "@/components/layout/site-header";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { t } from "@/lib/i18n/messages";
import { forgotPassword } from "@/lib/api/auth";
import { useDocumentTitle } from "@/hooks/use-document-title";

export default function ForgotPasswordPage() {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useDocumentTitle(`${t(locale, "forgotPasswordTitle")} | NCircle`);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(t(locale, "requiredField"));
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await forgotPassword({ email });
      if (res.success) {
        void navigate(`/verify-otp?email=${encodeURIComponent(email)}`);
      } else {
        setError(res.message ?? t(locale, "error"));
      }
    } catch {
      setError(t(locale, "error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SiteHeader />
      <main
        dir={isRTL ? "rtl" : "ltr"}
        className="flex items-center justify-center py-12 px-4 min-h-[calc(100vh-64px)] pt-24 bg-background"
      >
        <Card className="w-full max-w-[480px] p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-secondary to-brand-green" />
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {t(locale, "forgotPasswordTitle")}
            </h1>
            <p className="text-sm text-foreground-secondary">
              {t(locale, "forgotPasswordDesc")}
            </p>
          </div>
          <form onSubmit={onSubmit} className="space-y-6">
            <Input
              id="email"
              type="email"
              dir="ltr"
              label={`${t(locale, "email")} *`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={error}
              placeholder="partner.user@example.com"
            />
            <Button
              type="submit"
              className="w-full h-12 text-base"
              isLoading={submitting}
            >
              {t(locale, "sendOtp")}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm font-medium text-secondary hover:underline"
            >
              {t(locale, "backToLogin")}
            </Link>
          </div>
        </Card>
      </main>
      <ThemeToggle />
    </>
  );
}
